<template>
  <div
    :class="['flex flex-col gap-1 flex-1', !canReceiveChest && 'opacity-50']"
  >
    <div class="flex gap-1 items-center">
      <img
        :src="`/img/${imgMap[props.type]}.png`"
        alt=""
        class="w-4 h-4 img-render-nearest"
      />
      <div :class="['font-bold', rarityMap[props.type]]">
        {{ $t(`mission.equipment.boxType.${props.type}`) }}
      </div>
    </div>

    <div v-if="canReceiveChest" @click="clickClaimChest()">
      <button class="btn btn-xs btn-primary">
        {{ $t("Buttons.claim") }}
      </button>
    </div>

    <div v-else-if="countDown">
      {{
        $t("User.rewardCountDown", {
          hours: countDown.current.value.hours,
          minutes: countDown.current.value.minutes,
          seconds: countDown.current.value.seconds,
        })
      }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { EquipmentRarity } from "@/enums/mission";
import api from "@/modules/api";
import { useGlobalUser } from "@/stores/globalUser";
import { MateEquipment } from "@/types/entity";
import { AxiosError } from "axios";
import moment from "moment-timezone";
import { showConfirmDialog, showLoadingToast } from "vant";
import { useCountDown } from "@vant/use";
import { onActivated, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { ChestType } from "@/types/user";

const props = defineProps<{
  dtLastClaim: string;
  type: ChestType;
  rarity?: EquipmentRarity;
}>();

const emit = defineEmits<{
  (e: "equipmentClaimed", equipment: MateEquipment): void;
}>();

const imgMap = {
  [ChestType.Normal]: "energy-box-1",
  [ChestType.BasicMember]: "energy-box-4",
  [ChestType.StandardMember]: "energy-box-4",
  [ChestType.PremiumMember]: "energy-box-4",
};

const rarityMap = {
  [ChestType.Normal]: "rarity-1",
  [ChestType.BasicMember]: "rarity-4",
  [ChestType.StandardMember]: "rarity-4",
  [ChestType.PremiumMember]: "rarity-4",
};

const userStore = useGlobalUser();
const { t } = useI18n();
const router = useRouter();

const initRewardCount = () => {
  if (!props.dtLastClaim) {
    canReceiveChest.value = true;
  } else {
    allSeconds =
      moment(props.dtLastClaim).add(1, "day").diff(moment(), "second") + 2;
    if (allSeconds <= 0) {
      canReceiveChest.value = true;
    } else {
      startCountDown();
    }
  }
};

const canReceiveChest = ref(false);

let allSeconds = 0;
const countDown = useCountDown({
  time: 1000,
});

const startCountDown = () => {
  countDown.reset(allSeconds * 1000);
  countDown.start();
};

const clickClaimChest = async () => {
  const loading = showLoadingToast({
    message: t("Message.loading"),
    duration: 0,
    forbidClick: true,
  });

  if (canReceiveChest.value) {
    try {
      const res = await api.claimChest(props.type, props.rarity);
      await userStore.refresh();
      loading.close();
      emit("equipmentClaimed", res.itemzz[0]);
      canReceiveChest.value = false;
      allSeconds = moment().add(1, "day").diff(moment(), "second");
      startCountDown();
    } catch (e: unknown) {
      console.log(e);
      const error = e as AxiosError<{ message: string }>;
      const message = error.response?.data.message;
      if (
        message?.startsWith("装备背包容量不足") ||
        message?.startsWith("Not enough equipment inventory space")
      ) {
        showConfirmDialog({
          title: t("Message.notEnoughEquipmentInventorySpace"),
          message: t("Message.notEnoughEquipmentInventorySpaceDesc"),
          confirmButtonText: t("Buttons.confirm"),
          cancelButtonText: t("Buttons.cancel"),
        }).then(() => {
          router.push({
            name: "missionEquipment",
          });
        });
      }

      loading.close();
    }
  }
};
onActivated(() => {
  initRewardCount();
});
onMounted(() => {
  initRewardCount();
});
</script>
